import { _decorator, EventTarget, Enum, debug } from 'cc';
import { DEV } from 'cc/env';
import { IPlayable } from '../Misc/Processor';
import EnhanceAnimationClip from './EnhanceAnimationClip';

const { ccclass, property, disallowMultiple, help } = _decorator;

@ccclass
@disallowMultiple
@help(DEV && 'https://web.ttech.cc/docs/game-frontend-manual/art/component/enhanceAnimation.html')
export default class EnhanceAnimation extends IPlayable {
    public static readonly EVENT_TARGET: EventTarget = new EventTarget();

    @property({ tooltip: DEV && '預設的EnhanceAnimationClip名稱' })
    private readonly defaultClipName: string = '';

    @property({ tooltip: DEV && '自動播放預設動畫' })
    private readonly playOnLoad: boolean = false;

    @property
    private readonly allowMultiTrack: boolean = false;

    private _lastClip: EnhanceAnimationClip = null;

    public get clips() {
        return this.getComponents(EnhanceAnimationClip);
    }

    public get playingClips() {
        return this.clips.filter((clip) => clip.isRunning);
    }

    // TODO: 先不考慮MultiTrack
    public get currentTime() {
        return this.playingClips[0]?.currentTime ?? 0;
    }

    public start(): void {
        if (this.defaultClipName !== '' && this.playOnLoad) {
            this.play();
        }
    }

    public onDestroy() {
        this.stopAll();
    }

    /**
     * @param clipName clip to be played
     * @param startTime start time of the clip (in seconds)
     */
    public async play(clipName?: string, startTime = 0, skipAudioEffect = false) {
        if (!clipName) {
            clipName = this.defaultClipName;
        }
        const clip = this.clips.find((c) => c.clipName === clipName);
        if (clip) {
            if (!this.allowMultiTrack && this._lastClip) {
                this._lastClip.stop(false);
            }
            this._lastClip = clip;
            await clip.play(startTime, skipAudioEffect);
        } else {
            debug('EnhanceAnimationClip not found:', clipName);
        }
    }

    public stop(waitForFinish: boolean = false) {
        this._lastClip?.stop(waitForFinish);
    }

    public stopAll() {
        this.clips.forEach((clip) => {
            clip.stop(false);
        });
    }

    public waitForStart(clipName?: string) {
        return this.waitForEvent(EnhanceAnimationClip.EVENT_TYPE.PLAY, clipName);
    }

    public waitForFinish(clipName?: string) {
        return this.waitForEvent(EnhanceAnimationClip.EVENT_TYPE.FINISH, clipName);
    }

    public waitForEvent(eventType: string, clipName?: string) {
        return new Promise<void>((resolve) => {
            const callback = () => {
                if (clipName === this._lastClip?.clipName || !clipName) {
                    resolve();
                    this.node.off(eventType, callback);
                }
            };
            this.node.on(eventType, callback);
        });
    }

    public getClipsEnum() {
        const enumDef: { [key: string]: number } = { '&lt;None&gt;': 0 };
        const { clips } = this;
        for (let i = 0; i < clips.length; i++) {
            const { clipName } = clips[i];
            enumDef[clipName] = i + 1;
        }
        return Enum(enumDef);
    }
}
